import { Navigate, Outlet, Route, Routes } from "react-router-dom";

import AuthLayout from "@/layouts/AuthLayout";
import AppLayout from "@/layouts/AppLayout";
import ProtectedRoute from "@/layouts/ProtectedRoute";
import { useAuthStore } from "@/features/auth/store";
import LoginPage from "@/features/auth/pages/LoginPage";
import RegisterPage from "@/features/auth/pages/RegisterPage";
import ForgotPasswordPage from "@/features/auth/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/features/auth/pages/ResetPasswordPage";
import VerifyEmailPage from "@/features/auth/pages/VerifyEmailPage";
import { FeatureGuard } from "@/features/auth/access";
import DashboardPage from "@/features/dashboard/pages/DashboardPage";
import AgentsPage from "@/features/agents/pages/AgentsPage";
import AgentCreatePage from "@/features/agents/pages/AgentCreatePage";
import AgentDetailPage from "@/features/agents/pages/AgentDetailPage";
import AssistantsPage from "@/features/assistants/pages/AssistantsPage";
import AnalyticsPage from "@/features/analytics/pages/AnalyticsPage";
import LatencyPage from "@/features/latency/pages/LatencyPage";
import IntegrationsPage from "@/features/integrations/pages/IntegrationsPage";
import BillingPage from "@/features/billing/pages/BillingPage";
import PhoneNumbersPage from "@/features/telephony/pages/PhoneNumbersPage";
import SipTrunksPage from "@/features/telephony/pages/SipTrunksPage";
import WhatsAppPage from "@/features/telephony/pages/WhatsAppPage";
import ConversationsPage from "@/features/whatsapp/pages/ConversationsPage";
import SettingsPage from "@/features/settings/pages/SettingsPage";
import WebhooksPage from "@/features/webhooks/pages/WebhooksPage";
import CallsPage from "@/features/calls/pages/CallsPage";
import CallDetailPage from "@/features/calls/pages/CallDetailPage";
import FlowsPage from "@/features/flows/pages/FlowsPage";
import FlowEditorPage from "@/features/flows/pages/FlowEditorPage";
import KnowledgeBasesPage from "@/features/knowledge-bases/pages/KnowledgeBasesPage";
import KnowledgeBaseDetailPage from "@/features/knowledge-bases/pages/KnowledgeBaseDetailPage";
import NotificationsPage from "@/features/notifications/pages/NotificationsPage";
import TemplatesPage from "@/features/templates/pages/TemplatesPage";
import TemplateDetailPage from "@/features/templates/pages/TemplateDetailPage";
import CustomersPage from "@/features/customers/pages/CustomersPage";
import CustomerDetailPage from "@/features/customers/pages/CustomerDetailPage";
import CustomerPlansPage from "@/features/customers/pages/CustomerPlansPage";
import MyPlanPage from "@/features/customers/pages/MyPlanPage";
import AdminLayout, { SuperuserOnly } from "@/admin/AdminLayout";
import AdminTenantsPage from "@/admin/pages/AdminTenantsPage";
import AdminTenantDetailPage from "@/admin/pages/AdminTenantDetailPage";
import AdminUsersPage from "@/admin/pages/AdminUsersPage";
import AdminUserDetailPage from "@/admin/pages/AdminUserDetailPage";
import ResourceListPage from "@/admin/resources/ResourceListPage";
import AdminSettingsPage from "@/admin/pages/AdminSettingsPage";
import ResourceDetailPage from "@/admin/resources/ResourceDetailPage";

/**
 * Pages that belong to the tenant's team.
 *
 * Customers get the same dashboard as the tenant responsible for them (Peter,
 * 2026-07-28), scoped server-side to their own rows — so this wraps only what
 * is genuinely the tenant's own business: provider keys, team, wallet, other
 * customers, shared infrastructure. Anything here sends them to the dashboard
 * instead. The API denies them too; this is so they meet a page rather than a
 * wall of errors.
 */
function TenantOnly() {
  const role = useAuthStore((s) => s.user?.role);
  if (role === "customer") return <Navigate to="/dashboard" replace />;
  return <Outlet />;
}

function HomeRedirect() {
  return <Navigate to="/dashboard" replace />;
}

function NotFound() {
  return (
    <div className="grid place-items-center py-24 text-center">
      <div>
        <p className="text-sm font-medium text-muted-foreground">404</p>
        <h1 className="mt-2 text-2xl font-semibold tracking-tight">
          Page not found
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you&apos;re looking for doesn&apos;t exist or has moved.
        </p>
      </div>
    </div>
  );
}

function Placeholder({ title }: { title: string }) {
  return (
    <div className="space-y-2">
      <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
      <p className="text-sm text-muted-foreground">
        This screen will be ported next. Stack and routing are in place.
      </p>
    </div>
  );
}

export default function App() {
  return (
    <Routes>
      <Route element={<AuthLayout />}>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
      </Route>

      <Route element={<ProtectedRoute />}>
        <Route element={<AppLayout />}>
          <Route index element={<HomeRedirect />} />

          {/* Shared with customers. Each is scoped server-side to the calling
              customer's own rows — see core/customer_scope.py. A page belongs
              here only once its API is. */}
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/agents" element={<AgentsPage />} />
          <Route element={<FeatureGuard />}>
            <Route path="/agents/create" element={<AgentCreatePage />} />
          </Route>
          <Route path="/agents/:id" element={<AgentDetailPage />} />
          <Route path="/assistants" element={<AssistantsPage />} />
          <Route path="/assistants/:id" element={<AssistantsPage />} />
          <Route path="/flows" element={<FlowsPage />} />
          <Route path="/flows/:id" element={<FlowEditorPage />} />
          <Route path="/knowledge-bases" element={<KnowledgeBasesPage />} />
          <Route
            path="/knowledge-bases/:id"
            element={<KnowledgeBaseDetailPage />}
          />
          <Route path="/calls" element={<CallsPage />} />
          <Route path="/calls/:id" element={<CallDetailPage />} />
          <Route path="/phone-numbers" element={<PhoneNumbersPage />} />
          <Route path="/webhooks" element={<WebhooksPage />} />
          <Route path="/analytics" element={<AnalyticsPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/notifications" element={<NotificationsPage />} />

        <Route element={<TenantOnly />}>
          <Route path="/sip-trunks" element={<SipTrunksPage />} />
          <Route path="/whatsapp" element={<WhatsAppPage />} />
          <Route path="/conversations" element={<ConversationsPage />} />
          <Route path="/integrations" element={<IntegrationsPage />} />
          <Route path="/templates" element={<TemplatesPage />} />
          <Route path="/templates/:id" element={<TemplateDetailPage />} />
          <Route path="/latency" element={<LatencyPage />} />
          <Route path="/customers" element={<CustomersPage />} />
          <Route path="/customers/:id" element={<CustomerDetailPage />} />
          <Route path="/customer-plans" element={<CustomerPlansPage />} />
          <Route path="/billing" element={<BillingPage />} />
        </Route>

        {/* The one page only a customer has: the plan their provider sells them.
            A tenant user has no plan of their own, and the endpoint behind it
            answers 403 for them — the page redirects instead. */}
        <Route path="/my-plan" element={<MyPlanPage />} />

        </Route>
      </Route>

      {/* Admin area (replaces Django admin): its own shell, superusers only. */}
      <Route element={<ProtectedRoute />}>
        <Route element={<SuperuserOnly />}>
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<Navigate to="/admin/tenants" replace />} />
            <Route path="tenants" element={<AdminTenantsPage />} />
            <Route path="tenants/:id" element={<AdminTenantDetailPage />} />
            <Route path="users" element={<AdminUsersPage />} />
            <Route path="users/:id" element={<AdminUserDetailPage />} />
            <Route path="settings" element={<AdminSettingsPage />} />
            {/* Every other section runs on the generic engine. */}
            <Route path=":resource" element={<ResourceListPage />} />
            <Route path=":resource/:id" element={<ResourceDetailPage />} />
          </Route>
        </Route>
      </Route>

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}
